import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { Login } from '@/pages/Login/Login';
import { ErrorPage } from '@/pages/Error/ErrorPage';
import { Unauthorized } from '@/pages/Unauthorized/Unauthorized';
import { NotFound } from '@/pages/NotFound/NotFound';
import { ROUTES } from '@/lib/constants/routes';
import { ProtectedRoute } from './ProtectedRoute';
import { SizeChartsProtectedRoute } from './SizeChartsProtectedRoute';
import { InspectionReportDownloadRoute } from './InspectionReportDownloadRoute';
import { INSPECTION_REPORT_DOWNLOAD } from '@/lib/constants/routes';

// Lazy load pages
const Planning = lazy(() =>
  import('@/pages/Planning/Planning').then((m) => ({ default: m.Planning }))
);
const InspectorManagement = lazy(() =>
  import('@/pages/InspectorManagement/InspectorManagement').then((m) => ({
    default: m.InspectorManagement,
  }))
);
const InspectorDetail = lazy(() =>
  import('@/pages/InspectorManagement/InspectorDetail').then((m) => ({
    default: m.InspectorDetail,
  }))
);
const Configuration = lazy(() =>
  import('@/pages/Configuration/Configuration').then((m) => ({ default: m.Configuration }))
);
const InspectionDetailPage = lazy(() =>
  import('@/pages/InspectionDetail/InspectionDetail').then((m) => ({
    default: m.InspectionDetailPage,
  }))
);
const SizeChartsManagement = lazy(() =>
  import('@/pages/SizeChartsManagement/SizeChartsManagement').then((m) => ({
    default: m.SizeChartsManagement,
  }))
);
const MasterData = lazy(() =>
  import('@/pages/MasterData/MasterData').then((m) => ({ default: m.MasterData }))
);
const FactoryBulkCreate = lazy(() =>
  import('@/pages/MasterData/FactoryBulkCreate').then((m) => ({ default: m.FactoryBulkCreate }))
);
const InspectionReportDownloadPage = lazy(() =>
  import('@/pages/InspectionReportDownload/InspectionReportDownloadPage').then((m) => ({
    default: m.InspectionReportDownloadPage,
  }))
);

const LoadingFallback = () => (
  <div className="flex h-screen w-screen items-center justify-center bg-slate-50">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#05BFA0] border-t-transparent"></div>
  </div>
);

export const router = createBrowserRouter([
  {
    path: ROUTES.LOGIN,
    element: <Login />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.PLANNING,
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Planning />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.INSPECTOR_MANAGEMENT,
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <InspectorManagement />
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <InspectorDetail />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.CONFIGURATION,
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Configuration />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.SIZE_CHARTS_IMPORTS,
    element: <SizeChartsProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <SizeChartsManagement />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.MASTER_DATA,
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <MasterData />
              </Suspense>
            ),
          },
          {
            path: 'factories',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <MasterData />
              </Suspense>
            ),
          },
          {
            path: 'suppliers',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <MasterData />
              </Suspense>
            ),
          },
          {
            path: 'customers',
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <MasterData />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.MASTER_DATA_FACTORIES_BULK_CREATE,
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <FactoryBulkCreate />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: `${ROUTES.INSPECTION_DETAIL}/:id/${INSPECTION_REPORT_DOWNLOAD}`,
    element: <InspectionReportDownloadRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <InspectionReportDownloadPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES.INSPECTION_DETAIL}/:id`,
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <InspectionDetailPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.ERROR,
    element: <ErrorPage />,
  },
  {
    path: ROUTES.UNAUTHORIZED,
    element: <Unauthorized />,
  },
  {
    path: ROUTES.NOT_FOUND,
    element: <NotFound />,
  },
]);
